import { useState } from 'react';
import { Button, Card, CardContent, CardHeader, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, Heading, Stack, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Text, TextField, useToast } from '@chatsdet/ui';
import { Plus, Trash } from '@phosphor-icons/react';
import { useAllowedDomains, useCreateDomain, useDeleteDomain } from '../../../hooks/useInboxDomains';

export function DomainAllowlistManager() {
  const { data: domains, isLoading } = useAllowedDomains();
  const createDomain = useCreateDomain();
  const deleteDomain = useDeleteDomain();
  const { toast } = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const handleCreate = async () => {
    if (!newDomain.trim()) {
      toast.error('Domain is required');
      return;
    }

    try {
      await createDomain.mutateAsync({
        domain: newDomain.trim(),
        description: newDescription.trim() || undefined,
      });

      toast.success('Domain added successfully');
      setIsDialogOpen(false);
      setNewDomain('');
      setNewDescription('');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add domain');
    }
  };

  const handleDelete = async (domain: string) => {
    if (!confirm(`Are you sure you want to remove "${domain}" from the allowlist?`)) {
      return;
    }

    try {
      await deleteDomain.mutateAsync(domain);
      toast.success('Domain removed successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove domain');
    }
  };

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center">
          <div>
            <Heading size="sm">Email Domain Allowlist</Heading>
            <Text size="sm" color="muted">
              Only emails from these domains will be processed
            </Text>
          </div>

          <DialogTrigger isOpen={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Button variant="primary" size="sm">
              <Plus size={16} weight="bold" />
              Add Domain
            </Button>

            <Dialog>
              <DialogContent title="Add Allowed Domain">
                <Stack gap="md">
                  <TextField
                    label="Domain"
                    placeholder="example.com"
                    value={newDomain}
                    onChange={setNewDomain}
                    isRequired
                  />

                  <TextField
                    label="Description"
                    placeholder="Optional description"
                    value={newDescription}
                    onChange={setNewDescription}
                  />
                </Stack>

                <DialogFooter>
                  <Button variant="secondary" onPress={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onPress={handleCreate}
                    isDisabled={!newDomain.trim() || createDomain.isPending}
                  >
                    {createDomain.isPending ? 'Adding...' : 'Add Domain'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </DialogTrigger>
        </Flex>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Text>Loading...</Text>
        ) : !domains || domains.length === 0 ? (
          <Text color="muted">No allowed domains configured</Text>
        ) : (
          <Table>
            <TableHeader>
              <TableHead isRowHeader>Domain</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Added</TableHead>
              <TableHead aria-label="Actions"></TableHead>
            </TableHeader>
            <TableBody>
              {domains.map((domain) => (
                <TableRow key={domain.id}>
                  <TableCell>
                    <Text weight="medium">{domain.domain}</Text>
                  </TableCell>
                  <TableCell>
                    <Text color="muted">{domain.description || '—'}</Text>
                  </TableCell>
                  <TableCell>
                    <Text color="muted">
                      {new Date(domain.createdAt).toLocaleDateString()}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onPress={() => handleDelete(domain.domain)}
                      isDisabled={deleteDomain.isPending}
                    >
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
